/**
 * Military Layers
 * GeoJSON-based military location layers
 * Uses ensureGeoJsonLayer for deterministic, idempotent layer management.
 */

import type { Map as MapboxMap } from 'mapbox-gl';
import { ensureGeoJsonLayer, waitForStyleLoaded } from '../geoJsonLayerHelper';
import { imagePreloader } from '../ImagePreloader';

interface GeoJSONFeatureCollection {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    geometry: {
      type: 'Point';
      coordinates: [number, number];
    };
    properties: {
      id: number;
      name: string;
    };
  }>;
}

/**
 * Load GeoJSON and add layer to map with icon or circle fallback
 * Uses ImagePreloader to ensure icons are loaded only once
 */
async function addGeoJSONLayer(
  map: MapboxMap | null,
  geojsonPath: string,
  sourceId: string,
  layerId: string,
): Promise<void> {
  if (!map) return;

  // Wait for style to be loaded
  const styleReady = await waitForStyleLoaded(map);
  if (!styleReady) {
    console.warn(`Military [${layerId}]: style not ready, skipping`);
    return;
  }

  // Load GeoJSON data
  const response = await fetch(geojsonPath);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${geojsonPath}: ${response.status}`);
  }
  const geojson: GeoJSONFeatureCollection = await response.json();

  if (!geojson.features || geojson.features.length === 0) {
    // eslint-disable-next-line no-console
    // No features found for this military layer; skipping render.
    return;
  }

  const addWithIcon = (mapIconId: string) => {
    const ok = ensureGeoJsonLayer({
      map,
      sourceId,
      layerId,
      data: geojson,
      layerConfig: {
        type: 'symbol',
        layout: {
          'icon-image': mapIconId,
          'icon-size': ['interpolate', ['linear'], ['zoom'], 5, 0.25, 11, 0.6],
          'icon-allow-overlap': false,
          'icon-ignore-placement': false,
          visibility: 'visible',
        },
        paint: {},
      },
    });
    if (ok) {
      // eslint-disable-next-line no-console
      // Rendering icon-based military layer features.
    }
  };

  const addWithCircle = () => {
    const ok = ensureGeoJsonLayer({
      map,
      sourceId,
      layerId,
      data: geojson,
      layerConfig: {
        type: 'circle',
        paint: {
          'circle-radius': 5,
          'circle-color': '#ef4444',
          'circle-stroke-width': 0.5,
          'circle-stroke-color': '#ffffff',
        },
        layout: {
          visibility: 'visible',
        },
      },
    });
    if (ok) {
      // eslint-disable-next-line no-console
      // Rendering circle-based fallback for military layer features.
    }
  };

  // Use ImagePreloader to load the 'military:other' icon
  // This ensures the image is only loaded once, even if multiple layers use it
  const mapIconId = await imagePreloader.ensureIconLoaded(map, 'military:other');

  if (mapIconId && map.hasImage(mapIconId)) {
    addWithIcon(mapIconId);
  } else {
    console.warn(`Military [${layerId}]: icon not loaded, using circle fallback`);
    addWithCircle();
  }
}

/**
 * Add Chinese Air Force Locations layer
 */
export async function addAirForceLocations(map: MapboxMap | null): Promise<void> {
  await addGeoJSONLayer(
    map,
    '/data/airforce.geojson',
    'Chinese Air Force Locations $ Airports',
    'Chinese Air Force Locations $ Airports Layer',
  );
}

/**
 * Add Chinese PLA Institutes layer
 */
export async function addPLAInstitutes(map: MapboxMap | null): Promise<void> {
  await addGeoJSONLayer(
    map,
    '/data/pla_institutes.geojson',
    'Chinese PLA Institutes $ Universities',
    'Chinese PLA Institutes $ Universities Layer',
  );
}

/**
 * Add Chinese Command and Control layer
 */
export async function addCommandControl(map: MapboxMap | null): Promise<void> {
  await addGeoJSONLayer(
    map,
    '/data/command_control.geojson',
    'Chinese Command and Control $ Nodes',
    'Chinese Command and Control $ Nodes Layer',
  );
}

/**
 * Add Chinese Nuclear Explosion Sites layer
 */
export async function addNuclearSites(map: MapboxMap | null): Promise<void> {
  await addGeoJSONLayer(
    map,
    '/data/nuclear_sites.geojson',
    'Chinese Nuclear Explosion Sites $ Test Sites',
    'Chinese Nuclear Explosion Sites $ Test Sites Layer',
  );
}

/**
 * Add Chinese PLA Ground Force Bases layer
 */
export async function addPLAGroundForces(map: MapboxMap | null): Promise<void> {
  await addGeoJSONLayer(
    map,
    '/data/pla_ground_forces.geojson',
    'Chinese PLA Ground Force $ Bases Sites',
    'Chinese PLA Ground Force $ Bases Sites Layer',
  );
}

/**
 * Add Chinese PLA Plan Bases layer
 */
export async function addPLAPlanBases(map: MapboxMap | null): Promise<void> {
  await addGeoJSONLayer(
    map,
    '/data/pla_plan_bases.geojson',
    'Chinese PLA Plan Bases $ Bases Sites',
    'Chinese PLA Plan Bases $ Bases Sites Layer',
  );
}

/**
 * Add Chinese Overseas PLA Plan Bases layer
 */
export async function addOverseasPLAPlanBases(map: MapboxMap | null): Promise<void> {
  await addGeoJSONLayer(
    map,
    '/data/overseas_plan_bases.geojson',
    'Chinese Overseas PLA Plan Bases $ Bases Sites',
    'Chinese Overseas PLA Plan Bases $ Bases Sites Layer',
  );
}
